import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar } from "lucide-react";
import { useLang } from "../lib/LanguageContext";
import { BRAND } from "../lib/menuData";

export const BookingDialog = ({ variant = "primary" }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    date: "",
    time: "",
    guests: "",
    notes: "",
  });
  const [open, setOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = encodeURIComponent(
      `Halo Hayalan Coffee! Saya mau booking VIP Room.

Nama: ${form.name}
WhatsApp: ${form.phone}
Tanggal: ${form.date}
Jam: ${form.time}
Jumlah tamu: ${form.guests}
Catatan: ${form.notes || "-"}`
    );
    const wa = `https://wa.me/${BRAND.phone.replace("+", "")}?text=${message}`;
    window.open(wa, "_blank");
    setOpen(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const trigger =
    variant === "primary" ? (
      <button
        type="button"
        data-testid="open-booking-dialog"
        className="inline-flex items-center gap-2 bg-brand-coffee text-brand-cream px-6 py-3.5 rounded-full text-sm font-medium hover:bg-brand-ink transition-all hover:-translate-y-0.5"
      >
        <Calendar className="w-4 h-4" strokeWidth={1.8} />
        {t.booking.title}
      </button>
    ) : (
      <button
        type="button"
        data-testid="open-booking-dialog-secondary"
        className="text-sm font-medium text-brand-blue hover:underline"
      >
        {t.booking.title}
      </button>
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-brand-cream border-brand-border max-w-lg"
        data-testid="booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl md:text-3xl text-brand-ink tracking-tight">
            {t.booking.title}
          </DialogTitle>
          <DialogDescription className="text-brand-mocha">
            {t.booking.subtitle}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div>
            <Label htmlFor="b-name" className="text-xs uppercase tracking-widest text-brand-mocha">
              {t.booking.name}
            </Label>
            <Input
              id="b-name"
              data-testid="booking-name"
              required
              value={form.name}
              onChange={set("name")}
              className="mt-1.5 bg-white border-brand-border focus-visible:ring-brand-blue/40"
            />
          </div>
          <div>
            <Label htmlFor="b-phone" className="text-xs uppercase tracking-widest text-brand-mocha">
              {t.booking.phone}
            </Label>
            <Input
              id="b-phone"
              data-testid="booking-phone"
              type="tel"
              required
              value={form.phone}
              onChange={set("phone")}
              className="mt-1.5 bg-white border-brand-border focus-visible:ring-brand-blue/40"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="b-date" className="text-xs uppercase tracking-widest text-brand-mocha">
                {t.booking.date}
              </Label>
              <Input
                id="b-date"
                data-testid="booking-date"
                type="date"
                required
                value={form.date}
                onChange={set("date")}
                className="mt-1.5 bg-white border-brand-border focus-visible:ring-brand-blue/40"
              />
            </div>
            <div>
              <Label htmlFor="b-time" className="text-xs uppercase tracking-widest text-brand-mocha">
                {t.booking.time}
              </Label>
              <Input
                id="b-time"
                data-testid="booking-time"
                type="time"
                required
                value={form.time}
                onChange={set("time")}
                className="mt-1.5 bg-white border-brand-border focus-visible:ring-brand-blue/40"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="b-guests" className="text-xs uppercase tracking-widest text-brand-mocha">
              {t.booking.guests}
            </Label>
            <Input
              id="b-guests"
              data-testid="booking-guests"
              type="number"
              min="1"
              required
              value={form.guests}
              onChange={set("guests")}
              className="mt-1.5 bg-white border-brand-border focus-visible:ring-brand-blue/40"
            />
          </div>
          <div>
            <Label htmlFor="b-notes" className="text-xs uppercase tracking-widest text-brand-mocha">
              {t.booking.notes}
            </Label>
            <Textarea
              id="b-notes"
              data-testid="booking-notes"
              rows={3}
              value={form.notes}
              onChange={set("notes")}
              className="mt-1.5 bg-white border-brand-border focus-visible:ring-brand-blue/40 resize-none"
            />
          </div>

          <DialogFooter className="mt-2 gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="booking-cancel"
              className="px-5 py-3 rounded-full text-sm font-medium text-brand-ink hover:bg-brand-sand transition-colors"
            >
              {t.booking.cancel}
            </button>
            <button
              type="submit"
              data-testid="booking-submit"
              className="px-6 py-3 rounded-full text-sm font-medium bg-brand-blue text-white hover:bg-blue-800 transition-all"
            >
              {t.booking.submit}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
